import { useState, useEffect, useRef } from 'react';
import {
  Alert, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, List, ListItem, IconButton, Grid, Autocomplete, Select, MenuItem, styled, Typography, FormControl, InputLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import { getSuppliers } from '../../services/SupplierService';
import { addReplenishment, updateReplenishment, receivePendingReplenishment, uploadInvoiceFile, getDuplicateInvoiceReplenishments, getLastReplenishmentPrices } from '../../services/ReplenishmentService';
import { getSupplies } from '../../services/SuppliesService';
import { getProducts } from '../../services/ProductService';
import { paymentStatusOptions, getUnitById } from '../../utils/Enums';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import PriceDifferenceModal from './PriceDifferenceModal';
import { formatMoney, DEFAULT_CURRENCY } from '../../utils/currency';
import CurrencySelect from '../Currency/CurrencySelect';

const CenteredButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 20,
});

const fieldButtonSx = {
  minHeight: 56,
  height: 56
};

const getReplenishmentCategoryValue = (category) => {
  if (typeof category === 'number') return category;
  if (typeof category === 'string') return category === 'RETAIL_PRODUCTS' ? 2 : 1;
  if (category?.value) return category.value;
  return 1;
};

const getProviderId = (provider) => {
  if (!provider) return null;
  if (typeof provider === 'number' || typeof provider === 'string') return String(provider);
  return provider.id != null ? String(provider.id) : null;
};

const duplicateInvoiceSignature = (supplierId, invoice) =>
  `${supplierId || ''}:${String(invoice || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;

const itemBelongsToSupplier = (item, supplier) => {
  const supplierId = getProviderId(supplier);
  if (!supplierId) return false;
  return [item.primarySupplier, item.secondarySupplier]
    .map(getProviderId)
    .some(providerId => providerId === supplierId);
};

const getReplenishmentQuantity = (item, category) => {
  const stock = Number(item.stock ?? 0);
  const suggestedStock = Number(item.suggestedStock ?? 0);
  if (!Number.isFinite(stock) || !Number.isFinite(suggestedStock)) return 0;
  const missingQuantity = Math.max(0, suggestedStock - stock);
  if (missingQuantity <= 0) return 0;

  const presentationQuantity = category === 1
    ? Number(item.presentationQuantity ?? 0)
    : 1;
  const purchaseMultiple = Number.isFinite(presentationQuantity) && presentationQuantity > 0
    ? presentationQuantity
    : missingQuantity;

  return Math.ceil(missingQuantity / purchaseMultiple) * purchaseMultiple;
};

const formatQuantity = (quantity) => {
  const rounded = Math.round(quantity * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const AddReplenishmentModal = ({ locationId, open, onClose, onSave, defaultReplenishmentCategory = null, mode = 'create', replenishment = null, initialReceptionStatus = 1 }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [products, setProducts] = useState([]); // Para productos retail
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplyLot, setSupplyLot] = useState({});
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [priceDifferenceModalOpen, setPriceDifferenceModalOpen] = useState(false);
  const [priceDifferences, setPriceDifferences] = useState([]);
  const [pendingReplenishmentData, setPendingReplenishmentData] = useState(null);
  const [smartLoading, setSmartLoading] = useState(false);
  const [invoiceDuplicates, setInvoiceDuplicates] = useState([]);
  const [duplicateInvoiceDialogOpen, setDuplicateInvoiceDialogOpen] = useState(false);
  const [acknowledgedDuplicateInvoice, setAcknowledgedDuplicateInvoice] = useState(null);
  const isEditMode = mode === 'edit';
  const isReceiveMode = mode === 'receive';
  const isExistingMode = isEditMode || isReceiveMode;
  const isPendingOrder = !isReceiveMode && (
    initialReceptionStatus === 2 ||
    (isEditMode && replenishment?.receptionStatus === 2)
  );
  const [replenishmentCategory, setReplenishmentCategory] = useState(defaultReplenishmentCategory || 1); // 1 = SUPPLIES, 2 = RETAIL_PRODUCTS
  const submitLockRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  const [formData, setFormData] = useState({
    date: dayjs(),
    supplier: '',
    paymentStatus: 2,
    currency: DEFAULT_CURRENCY,
    invoice: '',
    items: []
  });

  // Actualizar categoría cuando cambie defaultReplenishmentCategory o se abra el modal
  useEffect(() => {
    if (open && defaultReplenishmentCategory !== null && defaultReplenishmentCategory !== undefined) {
      setReplenishmentCategory(defaultReplenishmentCategory);
    } else if (open && isExistingMode && replenishment) {
      setReplenishmentCategory(getReplenishmentCategoryValue(replenishment.replenishmentCategory));
    }
  }, [open, defaultReplenishmentCategory, isExistingMode, replenishment]);

  useEffect(() => {
    if (open) {
      // Cargar proveedores con paginación grande para obtener todos
      getSuppliers(1, 10000).then(response => {
        const suppliersArray = Array.isArray(response) ? response : (response?.providers || response?.data || []);
        // Filtrar proveedores según la categoría de reposición
        const currentCategory = replenishmentCategory;
        const filteredSuppliers = suppliersArray.filter(s => {
          let providerTypeValue = null;
          if (s.providerType) {
            if (typeof s.providerType === 'object' && s.providerType.value) {
              providerTypeValue = s.providerType.value;
            } else if (typeof s.providerType === 'number') {
              providerTypeValue = s.providerType;
            } else if (typeof s.providerType === 'string') {
              // Mapear nombre del enum a valor
              const enumMap = {
                'SUPPLIES_ONLY': 1,
                'RETAIL_ONLY': 2,
                'BOTH': 3
              };
              providerTypeValue = enumMap[s.providerType.toUpperCase()];
            }
          }
          // Si es reposición de insumos: SUPPLIES_ONLY (1) o BOTH (3)
          // Si es reposición de productos retail: RETAIL_ONLY (2) o BOTH (3)
          if (currentCategory === 1) {
            return providerTypeValue === 1 || providerTypeValue === 3;
          } else {
            return providerTypeValue === 2 || providerTypeValue === 3;
          }
        });
        setSuppliers(filteredSuppliers);
      }).catch(console.error);
      
      // Cargar suministros o productos según la categoría
      if (replenishmentCategory === 1) {
        // Reposición de insumos
        getSupplies(locationId, 1, 10000).then(response => {
          if (response && typeof response === 'object') {
            setSupplies(response.supplies || response.data || []);
          } else {
            setSupplies(response || []);
          }
        }).catch(console.error);
        setProducts([]);
      } else {
        // Reposición de productos retail
        getProducts(locationId, 1, 10000).then(response => {
          const productsArray = Array.isArray(response) ? response : (response?.products || response?.data || []);
          // Filtrar solo productos de tipo RETAIL
          // Gson serializa enums como strings, así que manejamos ambos casos
          const retailProducts = productsArray.filter(p => {
            let productTypeValue = null;
            if (p.productType) {
              if (typeof p.productType === 'object' && p.productType.value) {
                productTypeValue = p.productType.value;
              } else if (typeof p.productType === 'string') {
                productTypeValue = p.productType.toUpperCase();
              } else if (typeof p.productType === 'number') {
                // Si viene como número, mapear (aunque no debería)
                const typeMap = { 1: 'DEFINED', 2: 'UNDEFINED', 3: 'RETAIL' };
                productTypeValue = typeMap[p.productType] || p.productType;
              }
            }
            return productTypeValue === 'RETAIL';
          });
          setProducts(retailProducts);
        }).catch(console.error);
        setSupplies([]);
      }
      
      if (!isExistingMode) {
        resetForm();
      }
    }
  }, [open, locationId, replenishmentCategory, defaultReplenishmentCategory, isExistingMode]);

  useEffect(() => {
    if (!open || !isExistingMode || !replenishment) return;

    const category = getReplenishmentCategoryValue(replenishment.replenishmentCategory);
    const sourceItems = replenishment.items || [];
    const itemOptionsReady = category === 1 ? supplies.length > 0 : products.length > 0;
    if (sourceItems.length > 0 && !itemOptionsReady) return;

    const lotsByIndex = {};
    const mappedItems = sourceItems.map((item, index) => {
      lotsByIndex[index] = item.lot || '';
      const supply = item.supply
        ? (supplies.find(s => String(s.id) === String(item.supply.id)) || item.supply)
        : null;
      const product = item.product
        ? (products.find(p => String(p.id) === String(item.product.id)) || item.product)
        : null;

      return {
        id: `${replenishment.id}-${index}`,
        supply,
        product,
        quantity: item.quantity ?? '',
        unitPrice: item.unitPrice ?? '',
        expirationDate: item.expirationDate || null
      };
    });

    setReplenishmentCategory(category);
    setSelectedSupplier(
      suppliers.find(s => String(s.id) === String(replenishment.supplier?.id)) || replenishment.supplier || null
    );
    setFormData({
      date: replenishment.date ? dayjs(replenishment.date) : dayjs(),
      supplier: replenishment.supplier?.id || '',
      paymentStatus: replenishment.paymentStatus || 2,
      currency: replenishment.currency || DEFAULT_CURRENCY,
      invoice: replenishment.invoice || '',
      invoiceFile: replenishment.invoiceFile || '',
      items: mappedItems
    });
    setSupplyLot(lotsByIndex);
    setInvoiceFile(null);
    setLoading(false);
    setSmartLoading(false);
    setInvoiceDuplicates([]);
    setDuplicateInvoiceDialogOpen(false);
    setAcknowledgedDuplicateInvoice(null);
    submitLockRef.current = false;
  }, [open, isExistingMode, replenishment, suppliers, supplies, products]);

  const resetForm = () => {
    setFormData({
      date: dayjs(),
      supplier: '',
      paymentStatus: 2,
      currency: DEFAULT_CURRENCY,
      invoice: '',
      items: [],
    });
    setSupplyLot({});
    setSelectedSupplier(null);
    setInvoiceFile(null);
    setInvoiceDuplicates([]);
    setDuplicateInvoiceDialogOpen(false);
    setAcknowledgedDuplicateInvoice(null);
    setLoading(false);
    submitLockRef.current = false;
    idempotencyKeyRef.current = null;
    setReplenishmentCategory(defaultReplenishmentCategory || 1); // Resetear al valor por defecto o SUPPLIES
  };

  const handleInputChange = (e) => {
    if (e.target.name === 'invoice') {
      setAcknowledgedDuplicateInvoice(null);
    }
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setInvoiceFile(e.target.files[0]);
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
        ...prev,
        items: [...prev.items, { 
          id: Date.now(), 
          supply: null, 
          product: null, // Para productos retail
          quantity: '', 
          unitPrice: '',
          expirationDate: null // Para productos retail
        }]
    }));
  };

  const buildSmartReplenishmentItems = (supplier, category) => {
    const sourceItems = category === 1 ? supplies : products;
    return sourceItems
      .filter(item => category !== 1 || item.countsStock !== false)
      .filter(item => itemBelongsToSupplier(item, supplier))
      .map(item => ({
        item,
        quantity: getReplenishmentQuantity(item, category)
      }))
      .filter(({ quantity }) => quantity > 0)
      .map(({ item, quantity }) => ({
        id: `${category === 1 ? 'supply' : 'product'}-${item.id}-${Date.now()}`,
        supply: category === 1 ? item : null,
        product: category === 2 ? item : null,
        quantity: formatQuantity(quantity),
        unitPrice: '',
        expirationDate: null
      }));
  };

  const handleSupplierChange = (newSupplier) => {
    setSelectedSupplier(newSupplier);

    if (!newSupplier) {
      setFormData(prev => ({
        ...prev,
        supplier: '',
        items: []
      }));
      setSupplyLot({});
      setInvoiceDuplicates([]);
      setAcknowledgedDuplicateInvoice(null);
      return;
    }

    setFormData(prev => ({
      ...prev,
      supplier: newSupplier.id || ''
    }));
    setInvoiceDuplicates([]);
    setAcknowledgedDuplicateInvoice(null);
  };

  useEffect(() => {
    const invoice = String(formData.invoice || '').trim();
    if (!open || isExistingMode || isPendingOrder || !selectedSupplier?.id || !invoice) {
      setInvoiceDuplicates([]);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(async () => {
      try {
        const duplicates = await getDuplicateInvoiceReplenishments({
          locationId,
          supplierId: selectedSupplier.id,
          invoice,
        });
        if (active) setInvoiceDuplicates(Array.isArray(duplicates) ? duplicates : []);
      } catch (error) {
        // The server performs the same check on save, so a transient lookup error
        // must never prevent a legitimate manual replenishment.
        if (active) setInvoiceDuplicates([]);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, isExistingMode, isPendingOrder, locationId, selectedSupplier?.id, formData.invoice]);

  const handleSmartReplenishment = async () => {
    if (!selectedSupplier || isEditMode) return;
    setSmartLoading(true);
    try {
      const smartItems = buildSmartReplenishmentItems(selectedSupplier, replenishmentCategory);
      const priceRequestItems = smartItems.map(item => {
        const referenceId = replenishmentCategory === 1 ? item.supply?.id : item.product?.id;
        return {
          referenceType: replenishmentCategory === 1 ? 1 : 4,
          referenceId
        };
      }).filter(item => item.referenceId);
      const lastPrices = priceRequestItems.length > 0
        ? await getLastReplenishmentPrices(priceRequestItems, formData.currency)
        : {};
      const itemsWithLastPrices = smartItems.map(item => {
        const referenceId = replenishmentCategory === 1 ? item.supply?.id : item.product?.id;
        const referenceType = replenishmentCategory === 1 ? 1 : 4;
        const lastPrice = lastPrices[`${referenceType}-${referenceId}`];
        return {
          ...item,
          unitPrice: lastPrice != null ? String(lastPrice) : ''
        };
      });

      setFormData(prev => ({
        ...prev,
        supplier: selectedSupplier.id || '',
        items: itemsWithLastPrices
      }));
      setSupplyLot({});
    } finally {
      setSmartLoading(false);
    }
  };

  const smartReplenishmentReady = selectedSupplier && !isExistingMode && (
    replenishmentCategory === 1 ? supplies.length > 0 : products.length > 0
  );

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const updatedItems = prev.items.map((item, idx) => {
        if (idx === index) {
          const updatedItem = { ...item, [field]: value };
          // Actualizar unit según el tipo
          if (field === 'supply' && value) {
            updatedItem.unit = getUnitById(value.unit)?.label || '';
          } else if (field === 'product' && value) {
            updatedItem.unit = value.unit || '';
          }
          return updatedItem;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  
    // Asegurar que cada insumo/producto tenga sus propios lotes y no se mezclen
    if ((field === 'supply' || field === 'product') && value) {
      setSupplyLot(prev => ({
        ...prev,
        [index]: prev[index] || '' // Inicializa el lote para este índice
      }));
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index)
    }));
  };

  const handleLotChange = (index, lot) => {
    setSupplyLot(prev => ({
      ...prev,
      [index]: lot
    }));
  };  

  const calculateTotal = () => {
    return formData.items.reduce((acc, item) => acc + (parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)), 0);
  };

  const isValidForm = () => {
    if (!formData.date || !selectedSupplier) return false;
    if (!isPendingOrder && (!formData.paymentStatus || !formData.invoice)) return false;
    if (formData.items.length === 0) return false;
  
    for (let index = 0; index < formData.items.length; index++) {
      const item = formData.items[index];
      const hasValidUnitPrice = item.unitPrice !== '' &&
        item.unitPrice !== null &&
        item.unitPrice !== undefined &&
        Number.isFinite(Number(item.unitPrice));
      
      if (replenishmentCategory === 1) {
        // Validación para insumos
        if (!item.supply || !item.quantity || isNaN(item.quantity) || (!isPendingOrder && !hasValidUnitPrice)) {
          return false;
        }  
        const requiresLot = item.supply?.usesLot !== false;
        if (!isPendingOrder && requiresLot && (!supplyLot[index] || supplyLot[index].trim() === "")) {
          return false;
        }
        if (!isPendingOrder && requiresLot && !item.expirationDate) return false;
      } else {
        // Validación para productos retail
        if (!item.product || !item.quantity || isNaN(item.quantity) || (!isPendingOrder && !hasValidUnitPrice)) {
          return false;
        }
        const requiresLot = item.product?.usesLot === true;
        if (!isPendingOrder && requiresLot && (!supplyLot[index] || supplyLot[index].trim() === '')) {
          return false;
        }
        if (!isPendingOrder && requiresLot && !item.expirationDate) return false;
      }
    }
    return true;
  };

  const handleSave = async (forceUpdate = false, duplicateInvoiceAcknowledged = false) => {
    if (submitLockRef.current) {
      return;
    }
    const currentInvoiceSignature = duplicateInvoiceSignature(selectedSupplier?.id, formData.invoice);
    const duplicateAcknowledged = duplicateInvoiceAcknowledged || acknowledgedDuplicateInvoice === currentInvoiceSignature;
    if (!isExistingMode && !isPendingOrder && invoiceDuplicates.length > 0 && !duplicateAcknowledged) {
      setDuplicateInvoiceDialogOpen(true);
      return;
    }
    submitLockRef.current = true;
    console.log("🔍 [AddReplenishmentModal] handleSave llamado con forceUpdate:", forceUpdate, "tipo:", typeof forceUpdate);
    setLoading(true);
    const totalPrice = calculateTotal();

    // Extraer solo valores primitivos para evitar referencias circulares
    const supplierId = selectedSupplier?.id ? parseInt(selectedSupplier.id) : null;
    
    // Asegurar que forceUpdate sea explícitamente false si no se pasa o es undefined
    const forceUpdateValue = forceUpdate === true ? true : false;
    console.log("🔍 [AddReplenishmentModal] forceUpdateValue final:", forceUpdateValue);
    
    // Construir el objeto base - siempre incluir force_update explícitamente
    // Declarar fuera del try para que esté disponible en el catch
    const idempotencyKey = isExistingMode ? null : (idempotencyKeyRef.current || pendingReplenishmentData?.idempotency_key || uuidv4());
    if (!isExistingMode) {
      idempotencyKeyRef.current = idempotencyKey;
    }
    const replenishmentData = {
        location_id: parseInt(locationId),
        replenishment_date: formData.date && dayjs(formData.date).isValid() 
          ? dayjs(formData.date).format('YYYY-MM-DDTHH:mm:ss')
          : null,
        supplier_id: supplierId,
        payment_status: parseInt(formData.paymentStatus),
        paid_amount: isExistingMode ? (replenishment?.paidAmount ?? null) : null,
        last_payment_date: isExistingMode && replenishment?.lastPaymentDate
          ? dayjs(replenishment.lastPaymentDate).format('YYYY-MM-DDTHH:mm:ss')
          : null,
        payment_method: isExistingMode ? (replenishment?.paymentMethod || null) : null,
        payment_notes: isExistingMode ? (replenishment?.paymentNotes || null) : null,
        total_price: parseFloat(totalPrice),
        currency: formData.currency || DEFAULT_CURRENCY,
        invoice: String(formData.invoice || ''),
        replenishment_category: replenishmentCategory, // 1 = SUPPLIES, 2 = RETAIL_PRODUCTS
        reception_status: isReceiveMode ? 1 : (isEditMode ? (replenishment?.receptionStatus ?? 1) : initialReceptionStatus),
        force_update: forceUpdateValue, // Siempre enviar explícitamente (false por defecto)
        duplicate_invoice_acknowledged: duplicateAcknowledged,
        idempotency_key: idempotencyKey,
        items: formData.items.map((item, index) => {
          const quantity = item.quantity ? parseFloat(item.quantity) : 0;
          const unitPrice = item.unitPrice ? parseFloat(item.unitPrice) : 0;
          const expirationDate = item.expirationDate && dayjs(item.expirationDate).isValid()
            ? dayjs(item.expirationDate).format('YYYY-MM-DDTHH:mm:ss')
            : null;
          const lot = supplyLot[index] ? String(supplyLot[index]) : '';
          
          if (replenishmentCategory === 1) {
            // Reposición de insumos
            const supplyId = item.supply?.id ? parseInt(item.supply.id) : null;
            const requiresLot = item.supply?.usesLot !== false;
            return {
              supply_id: supplyId,
              reference_type: 1, // SUPPLY
              reference_id: supplyId,
              quantity: quantity,
              unit_price: unitPrice,
              expiration_date: requiresLot ? expirationDate : null,
              lot: requiresLot ? lot : null
            };
          } else {
            // Reposición de productos retail
            const productId = item.product?.id ? parseInt(item.product.id) : null;
            const requiresLot = item.product?.usesLot === true;
            return {
              product_id: productId,
              reference_type: 4, // PRODUCT
              reference_id: productId,
              quantity: quantity,
              unit_price: unitPrice,
              expiration_date: requiresLot ? expirationDate : null,
              lot: requiresLot ? lot : null
            };
          }
        }),
      };

    try {
      // Validación extra antes de enviar
      if (!replenishmentData.replenishment_date) {
        console.error("❌ Error: Fecha inválida en la request", replenishmentData);
        alert("Error: La fecha de reposición es inválida.");
        setLoading(false);
        submitLockRef.current = false;
        return;
      }
      
      // Para insumos con lote, expiration_date es obligatoria
      if (!isPendingOrder && replenishmentCategory === 1 && replenishmentData.items.some((item, index) => (formData.items[index]?.supply?.usesLot !== false) && !item.expiration_date)) {
        console.error("❌ Error: Fecha de expiración inválida en la request", replenishmentData);
        alert("Error: Todas las fechas de expiración son obligatorias para insumos.");
        setLoading(false);
        submitLockRef.current = false;
        return;
      }
   
      console.log("🔍 [AddReplenishmentModal] Enviando reposición - forceUpdate:", forceUpdate);
      console.log("🔍 [AddReplenishmentModal] Datos a enviar:", JSON.stringify(replenishmentData, null, 2));
      const savedData = isReceiveMode
        ? await receivePendingReplenishment(replenishment.id, replenishmentData)
        : isEditMode
          ? await updateReplenishment(replenishment.id, replenishmentData)
          : await addReplenishment(replenishmentData);
  
      if (invoiceFile) {
        await uploadInvoiceFile(invoiceFile, savedData.id);
      }
  
      onSave(savedData);
      onClose();
      setPriceDifferenceModalOpen(false);
      setPriceDifferences([]);
      setPendingReplenishmentData(null);
      idempotencyKeyRef.current = null;
    } catch (error) {
      console.error('Error saving replenishment:', error);
      
      // Verificar si es un error de diferencia de precio
      if (error.message === 'PRICE_DIFFERENCE_WARNING' && error.priceDifferences) {
        setPriceDifferences(error.priceDifferences);
        // Guardar los datos SIN force_update para que la próxima vez se valide de nuevo
        const { force_update, ...dataWithoutForceUpdate } = replenishmentData;
        setPendingReplenishmentData(dataWithoutForceUpdate);
        setPriceDifferenceModalOpen(true);
        setLoading(false);
        submitLockRef.current = false;
        return;
      }

      if (error.message === 'DUPLICATE_INVOICE_WARNING') {
        setDuplicateInvoiceDialogOpen(true);
        setLoading(false);
        submitLockRef.current = false;
        return;
      }
      
      setLoading(false);
      submitLockRef.current = false;
      return;
    } finally {
      if (!priceDifferenceModalOpen) {
        submitLockRef.current = false;
      }
    }
  };

  const handleConfirmPriceDifference = async () => {
    setPriceDifferenceModalOpen(false);
    if (pendingReplenishmentData) {
      console.log("🔍 [AddReplenishmentModal] Confirmando diferencia de precio - reenviando con forceUpdate: true");
      // Reenviar con forceUpdate = true
      await handleSave(true);
    }
  };  

  const handleConfirmDuplicateInvoice = async () => {
    setAcknowledgedDuplicateInvoice(duplicateInvoiceSignature(selectedSupplier?.id, formData.invoice));
    setDuplicateInvoiceDialogOpen(false);
    await handleSave(false, true);
  };

  const handleDialogClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isReceiveMode ? 'Completar ingreso de pedido' : (isEditMode ? (t('edit_replenishment_items') || 'Editar reposición') : (isPendingOrder ? 'Nuevo pedido' : 'Nueva reposición'))}</DialogTitle>
      <DialogContent>
            <Grid container spacing={2}>
              {defaultReplenishmentCategory === null && (
                <Grid item xs={12} style={{marginTop: "8px"}}>
                  <FormControl fullWidth>
                    <InputLabel>{t('replenishment_category') || 'Categoría de Reposición'}</InputLabel>
                    <Select
                      value={replenishmentCategory}
                      onChange={(e) => {
                        setReplenishmentCategory(e.target.value);
                        // Limpiar items cuando cambia la categoría
                        setFormData(prev => ({ ...prev, items: [] }));
                        setSelectedSupplier(null);
                        setSupplyLot({});
                      }}
                      label={t('replenishment_category') || 'Categoría de Reposición'}
                      disabled={loading}
                    >
                      <MenuItem value={1}>{t('supplies') || 'Insumos'}</MenuItem>
                      <MenuItem value={2}>{t('retail_products') || 'Productos Retail'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={4} style={{marginTop: "8px"}}>
                <DatePicker
                  label={t('date')}
                  value={formData.date ? dayjs(formData.date, 'YYYY-MM-DDTHH:mm:ss') : null}
                  onChange={(date) => setFormData({ ...formData, date: date ? date.format('YYYY-MM-DDTHH:mm:ss') : '' })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  format="DD/MM/YYYY"
                />
              </Grid>
              <Grid item xs={12} md={4} style={{marginTop: "8px"}}>
                <Autocomplete
                  value={selectedSupplier}
                  onChange={(event, newValue) => handleSupplierChange(newValue)}
                  options={suppliers || []}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => <TextField {...params} label={t('supplier')} fullWidth />}
                  disabled={loading}
                />
              </Grid>
              {isPendingOrder && <Grid item xs={12} md={4} style={{marginTop: "8px", display: 'flex', alignItems: 'center'}}>
                <Button
                  variant="outlined"
                  startIcon={getIcon('refresh')}
                  onClick={handleSmartReplenishment}
                  disabled={loading || smartLoading || !smartReplenishmentReady}
                  fullWidth
                  sx={fieldButtonSx}
                >
                  {t('replenish_to_suggested') || 'Reponer hasta sugerido'}
                </Button>
              </Grid>}
              {!isPendingOrder && <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label={t('invoice')}
                  name="invoice"
                  value={formData.invoice}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={loading}
                />
              </Grid>}
              {!isPendingOrder && invoiceDuplicates.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Esta factura ya figura en {invoiceDuplicates.length} reposición{invoiceDuplicates.length === 1 ? '' : 'es'} activa{invoiceDuplicates.length === 1 ? '' : 's'} para este proveedor. Revisala antes de continuar.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={4} style={{marginTop: "8px"}}>
                <CurrencySelect
                  size="medium"
                  value={formData.currency}
                  onChange={(currency) => setFormData({ ...formData, currency })}
                  disabled={loading || isExistingMode}
                />
              </Grid>
              {!isPendingOrder && <Grid item xs={12} sm={4} style={{marginTop: "8px"}}>
                <FormControl fullWidth>
                  <InputLabel id="payment-status-label">{t('payment_status')}</InputLabel>
                <Select
                  labelId="payment-status-label"
                  name="paymentStatus"
                  label={t('payment_status')}
                  value={formData.paymentStatus}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={loading}
                >
                  {paymentStatusOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>{t(option.label)}</MenuItem>
                  ))}
                </Select>
                </FormControl>
              </Grid>}
              {!isPendingOrder && <Grid item xs={12} sm={4}  style={{marginTop: "8px", display: 'flex', alignItems: 'center'}}>
                <input
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  id="upload-file"
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <label htmlFor="upload-file" style={{ width: '100%' }}>
                  <Button variant="outlined" color="primary" component="span" startIcon={getIcon('document')} disabled={loading} fullWidth sx={fieldButtonSx}>
                    <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 }}>
                      {invoiceFile ? invoiceFile.name : formData.invoiceFile ? 'Factura adjunta (cambiar)' : t('upload_file')}
                    </span>
                  </Button>
                </label>
              </Grid>}
            </Grid>
            <List>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 1000 }}>
                  <List>
                    {formData.items.map((item, index) => (
                      <ListItem key={item.id} sx={{ display: 'flex', gap: 2 }}>
                        {replenishmentCategory === 1 ? (
                          <Autocomplete
                            options={supplies || []}
                            getOptionLabel={(option) => option?.name || ''}
                            value={item.supply || null}
                            onChange={(event, newValue) => handleItemChange(index, 'supply', newValue || '')}
                            renderInput={(params) => <TextField {...params} label={t('supply')} fullWidth />}
                            sx={{ flex: 2 }}
                            disabled={loading}
                          />
                        ) : (
                          <Autocomplete
                            options={products || []}
                            getOptionLabel={(option) => option?.name || ''}
                            value={item.product || null}
                            onChange={(event, newValue) => handleItemChange(index, 'product', newValue || '')}
                            renderInput={(params) => <TextField {...params} label={t('product')} fullWidth />}
                            sx={{ flex: 2 }}
                            disabled={loading}
                          />
                        )}
                        <TextField
                          label={t('quantity')}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          sx={{ flex: 1.5 }}
                          disabled={loading}
                          InputProps={{
                            endAdornment: <Typography variant="body2" sx={{ marginLeft: 1 }}>
                              {replenishmentCategory === 1 
                                ? (item.supply?.unit != null ? `(${getUnitById(item.supply.unit).label})` : '')
                                : (item.product?.unit != null ? `(${getUnitById(item.product.unit).label})` : '')
                              }
                            </Typography>
                          }}
                        />
                        {!isPendingOrder && <TextField
                          label={t('unit_price')}
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          sx={{ flex: 1 }} // 15%
                          disabled={loading}
                        />}
                        {!isPendingOrder && (replenishmentCategory === 1 ? item.supply?.usesLot !== false : item.product?.usesLot === true) ? (
                          <>
                            <DatePicker
                              label={t('expiration_date')}
                              value={item.expirationDate ? dayjs(item.expirationDate, 'YYYY-MM-DDTHH:mm:ss') : null}
                              onChange={(date) => handleItemChange(index, 'expirationDate',
                                date && dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss') : null)}
                              renderInput={(params) => <TextField {...params} fullWidth />}
                              format="DD/MM/YYYY"
                              sx={{ flex: 1.2 }}
                              disabled={loading}
                            />
                            <TextField
                              label={t('lot_number')}
                              value={supplyLot[index] || ''}
                              onChange={(e) => handleLotChange(index, e.target.value)}
                              fullWidth
                              sx={{ flex: 1 }}
                              required
                              disabled={loading}
                            />
                          </>
                        ) : !isPendingOrder && (
                          <Box sx={{ flex: 2.2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                            {t('no_lot_required')}
                          </Box>
                        )}
                        <IconButton onClick={() => handleRemoveItem(index)} disabled={loading}>{getIcon('close')}</IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
              <CenteredButtonContainer>
                <Button variant="outlined" startIcon={getIcon('add')} onClick={handleAddItem} disabled={loading}>
                  {t('add_item')}
                </Button>
              </CenteredButtonContainer>
            </List>
            <Typography variant="h6" style={{ marginTop: 20, textAlign: 'right' }}>
          {!isPendingOrder && `${t('total')}: ${formatMoney(calculateTotal(), formData.currency)}`}
            </Typography>
          
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={loading}>{t('cancel')}</Button>
        <LoadingButton loading={loading} disabled={loading || !isValidForm()} onClick={() => handleSave(false)} variant="contained">
          {isReceiveMode ? 'Confirmar ingreso' : (isEditMode ? t('save') : (isPendingOrder ? 'Guardar pedido' : 'Hacer reposición'))}
        </LoadingButton>
      </DialogActions>
      
      <PriceDifferenceModal
        open={priceDifferenceModalOpen}
        onClose={() => {
          setPriceDifferenceModalOpen(false);
          setPriceDifferences([]);
          setPendingReplenishmentData(null);
        }}
        onConfirm={handleConfirmPriceDifference}
        priceDifferences={priceDifferences}
        loading={loading}
      />
      <Dialog open={duplicateInvoiceDialogOpen} onClose={loading ? undefined : () => setDuplicateInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Factura posiblemente repetida</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ya existe una reposición activa con este número de factura para el proveedor seleccionado. Continuar puede duplicar stock, costos o pagos.
          </Alert>
          {invoiceDuplicates.map((duplicate) => (
            <Typography key={duplicate.id} variant="body2" sx={{ mb: 0.5 }}>
              Reposición #{duplicate.id} · {dayjs(duplicate.date).format('DD/MM/YYYY')} · {formatMoney(Number(duplicate.totalPrice || 0), duplicate.currency)}
              {duplicate.createdFromReceivedInvoice ? ' · creada desde factura recibida' : ''}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateInvoiceDialogOpen(false)} disabled={loading}>Revisar</Button>
          <Button onClick={handleConfirmDuplicateInvoice} disabled={loading} color="warning" variant="contained">Continuar de todos modos</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddReplenishmentModal;
